import { historyExtension } from '@copera/tiptap/extensions';
import { useBoardTableRowDialog } from '@modules/board/BoardTableRowDialog';
import { basicExtensions } from '@modules/editor';
import { chatImagePasteExtension } from '@modules/editor/extensions/chatImagePaste';
import { codeblockExtension } from '@modules/editor/extensions/codeblock';
import { emojiSearchExtension } from '@modules/editor/extensions/emojiSearch';
import { mentionTableRowExtension } from '@modules/editor/extensions/mentionTableRow';
import { slashCommandExtension } from '@modules/editor/extensions/slashCommand';
import { userMentionExtension } from '@modules/editor/extensions/userMention';
import { useWorkspaceInfoContextStore } from '@modules/workspace/providers/WorkspaceInfo';
import type { EditorOptions } from '@tiptap/core';
import { useMemo } from 'react';
import type { IWorkspaceUser } from 'src/@types/workspace-info';

type Props = {
  participants: IWorkspaceUser[];
  handleChatUploadFile?: (file: File) => void;
  placeholder?: string;
  enableSpaceForAi?: boolean;
  enableSlashCommand?: boolean;
  enableCodeBlock?: boolean;
};

export const useConversationEditorConfig = ({
  handleChatUploadFile,
  participants,
  placeholder = "Write a message or press 'space' for AI. Use @ to quote a user...",
  enableSpaceForAi = true,
  enableSlashCommand = true,
  enableCodeBlock = true,
}: Props) => {
  const { showBoardTableRowDialog } = useBoardTableRowDialog();
  const workspaceStore = useWorkspaceInfoContextStore();
  const extensions = useMemo(() => {
    const items = [
      ...basicExtensions,
      userMentionExtension,
      emojiSearchExtension,
      mentionTableRowExtension,
      chatImagePasteExtension,
      historyExtension,
    ];

    if (enableSlashCommand) {
      items.push(slashCommandExtension);
    }

    if (enableCodeBlock) {
      items.push(codeblockExtension);
    }

    return items;
  }, [enableSlashCommand, enableCodeBlock]);

  const editorConfig = useMemo(() => {
    return {
      extensions,
      content: '',
      extensionConfig: {
        enterBehave: {
          createNewLine: false,
        },
        aiPromptOptions: {
          enableSpaceForAi: enableSpaceForAi,
          position: 'fixed',
        },
        placeholder,
        mentionTableRow: {
          onClick(rowId: string, boardId: string, tableId: string) {
            showBoardTableRowDialog({
              viewId: '',
              boardId,
              tableId,
              rowId,
            });
          },
        },
        userMention: {
          enabled: true,
          mentionableUsers: participants,
        },
        chatUploadFile: handleChatUploadFile,
      },
      externalStores: {
        workspaceStore,
      },
    } satisfies Partial<EditorOptions>;
  }, [
    participants,
    handleChatUploadFile,
    placeholder,
    enableSpaceForAi,
    extensions,
    showBoardTableRowDialog,
    workspaceStore,
  ]);

  return { editorConfig };
};
